export const HAN_CHARACTER_PATTERN = /[㐀-鿿豈-﫿]/;

export const BANNED_TECHNICAL_TOKENS: readonly string[] = Object.freeze([
  "pkg:",
  "awaits deterministic",
  "awaits",
  "deterministic",
  "source:",
  "sourceRefs",
  "ruleTrace",
  "inputHash",
  "pii_ciphertext",
  "report_inputs",
  "method:",
  "package:",
  "convention:",
  "code:",
  "blocked",
  "name/phone",
  "lunar-javascript",
  "iztro",
  "pythagoras-core",
  "mai-hoa-time-cast",
  "bazi-solar",
  "ziwei-solar",
  "undefined",
  "NaN",
  "[object Object]",
  "null",
  "snapshot",
  "golden fixtures",
  "golden test",
  "ruleset",
  "baseline",
  "report version",
  "modulo",
  "fixture",
  "fixtures",
  "corpus",
  "core",
  "Latin",
]);

const WORD_BANNED_TOKENS = new Set([
  "awaits",
  "deterministic",
  "blocked",
  "undefined",
  "NaN",
  "null",
  "snapshot",
  "ruleset",
  "baseline",
  "modulo",
  "fixture",
  "fixtures",
  "corpus",
  "core",
  "Latin",
]);

const VIETNAMESE_TERM_REPLACEMENTS: readonly Readonly<
  [pattern: RegExp, replacement: string]
>[] = Object.freeze([
  [/\bgolden fixtures?\b/gi, "bộ mẫu chuẩn đã ký"],
  [/\bgolden test\b/gi, "kiểm thử mẫu chuẩn"],
  [/\bsnapshot\b/gi, "bản chụp"],
  [/\bruleset\b/gi, "bộ quy tắc"],
  [/\bbaseline\b/gi, "mốc cơ sở"],
  [/\breport version\b/gi, "phiên bản báo cáo"],
  [/\bmodulo\b/gi, "phép chia lấy dư"],
  [/\bfixtures?\b/gi, "bộ mẫu chuẩn"],
  [/\bcorpus\b/gi, "kho tư liệu"],
  [/\bcore\b/gi, "cốt lõi"],
  [/\bLatin\b/g, "La-tinh"],
]);

export type TextGuardResult = Readonly<{
  textVi: string;
  flagged: boolean;
  technicalDetail?: string;
}>;

function containsForbiddenScript(value: string): boolean {
  return HAN_CHARACTER_PATTERN.test(value);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function findBannedTechnicalTokenIndex(
  value: string,
  token: string,
  fromIndex = 0,
): number {
  if (!WORD_BANNED_TOKENS.has(token)) {
    return value.toLowerCase().indexOf(token.toLowerCase(), fromIndex);
  }

  const flags = token === "NaN" || token === "Latin" ? "g" : "gi";
  const pattern = new RegExp(`\\b${escapeRegExp(token)}\\b`, flags);
  pattern.lastIndex = fromIndex;
  return pattern.exec(value)?.index ?? -1;
}

function containsForbiddenTechnicalToken(value: string): boolean {
  return BANNED_TECHNICAL_TOKENS.some(
    (token) => findBannedTechnicalTokenIndex(value, token) >= 0,
  );
}

function translateTechnicalTerms(value: string): string {
  return VIETNAMESE_TERM_REPLACEMENTS.reduce(
    (translated, [pattern, replacement]) =>
      translated.replace(pattern, replacement),
    value,
  );
}

export function isSafeVietnameseDisplayText(value: string): boolean {
  return (
    !containsForbiddenScript(value) && !containsForbiddenTechnicalToken(value)
  );
}

export function guardVietnameseDisplayText(
  candidateVi: string,
  fallbackVi: string,
): TextGuardResult {
  const translatedCandidate = translateTechnicalTerms(candidateVi);
  if (isSafeVietnameseDisplayText(translatedCandidate)) {
    return Object.freeze({ textVi: translatedCandidate, flagged: false });
  }

  return Object.freeze({
    textVi: fallbackVi,
    flagged: true,
    technicalDetail: candidateVi,
  });
}
